/**
 * The Hub page -> WTC Hub extension unlock hand-off protocol (D-13; extension
 * VAULT.md "Hub contract"). Hub sends these over `chrome.runtime.sendMessage`
 * (`externally_connectable`); the extension accepts them only from its build's
 * single trusted Hub origin and only from a page under `/vault` (review M-new-2).
 *
 * The unlock token is a bearer step-up token, not a secret payload. It never
 * reaches Hub's server, a content script, or any model-callable tool.
 */
import { z } from "zod";

import { DeviceSettings } from "./device-settings.js";

export const UnlockMessage = z.discriminatedUnion("type", [
  z.object({ type: z.literal("WTC_VAULT_UNLOCK"), unlockToken: z.string().min(20).max(4096), expiresAt: z.iso.datetime() }),
  z.object({ type: z.literal("WTC_VAULT_LOCK") }),
]);
export type UnlockMessage = z.infer<typeof UnlockMessage>;

/**
 * D-22: Hub web and the extension in the same browser are ONE device, so they share the
 * non-secret device settings ("Lock after", clipboard clear). Hub sends its record; the
 * extension keeps the newer one (by `updatedAt`) and answers `{ ok: true, settings }` with it.
 * Same sender gate as `UnlockMessage` (trusted Hub origin, page under /vault). No secrets.
 */
export const SettingsSyncMessage = z.object({ type: z.literal("WTC_VAULT_SETTINGS_SYNC"), settings: DeviceSettings });
export type SettingsSyncMessage = z.infer<typeof SettingsSyncMessage>;
export const SettingsSyncReply = z.object({ ok: z.literal(true), settings: DeviceSettings });
export type SettingsSyncReply = z.infer<typeof SettingsSyncReply>;

/** True when `pathname` is `/vault` or a path beneath it (`/vault/...`) — never `/vaultx`. */
export function isVaultPath(pathname: string): boolean {
  return pathname === "/vault" || pathname.startsWith("/vault/");
}

/** What Chrome reports about an external message's sender (it fills these itself). */
export interface ExternalSender {
  readonly origin?: string | undefined;
  readonly url?: string | undefined;
  readonly id?: string | undefined;
}

/**
 * Every `WTC_VAULT_*` external message must come from one of `trustedOrigins`
 * AND from a page under `/vault`. The origin is checked as reported AND as
 * parsed out of the URL; both must agree. Our own extension pages never use the
 * external channel.
 */
export function isTrustedUnlockSender(sender: ExternalSender, ownId: string, trustedOrigins: readonly string[]): boolean {
  if (sender.id === ownId) return false;
  if (typeof sender.origin !== "string" || !trustedOrigins.includes(sender.origin)) return false;
  if (typeof sender.url !== "string") return false;
  let url: URL;
  try {
    url = new URL(sender.url);
  } catch {
    return false;
  }
  if (url.origin !== sender.origin) return false;
  if (url.username !== "" || url.password !== "") return false;
  return isVaultPath(url.pathname);
}
